import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import './Signup.css';
import GoogleLogin from './GoogleLogin';
import logo from '../../assets/logo.png';

const Signup = () => {
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState({ type: '', text: '' });
    const [isButtonLoading, setIsButtonLoading] = useState(false);
    const navigate = useNavigate();

    // Function to handle errors and display appropriate messages
    const handleError = (error) => {
        console.error('Signup error:', error);
        
        // Get HTTP error code and message
        const statusCode = error.response?.status;
        const errorMessage = error.response?.data?.msg;
        
        // Specific error messages based on status code
        switch (statusCode) {
            case 400:
                setMessage({
                    type: 'error',
                    text: errorMessage || 'Please provide a valid email address.'
                });
                break;
            case 409:
                setMessage({
                    type: 'error',
                    text: 'This email is already registered. Try logging in instead, or reset your password if needed.'
                });
                break;
            case 500:
                setMessage({
                    type: 'error',
                    text: 'Server error occurred. Please try again later or contact support if the problem persists.'
                });
                break;
            default:
                setMessage({
                    type: 'error',
                    text: errorMessage || 'An error occurred while creating your account. Please try again.'
                });
        }
    };

    const handleSignup = async (e) => {
        e.preventDefault();
        setIsButtonLoading(true);
        setMessage({ type: '', text: '' }); // Clear previous messages
        
        try {
            const response = await api.post('/signup', { email });
            setMessage({
                type: 'success',
                text: 'Account created successfully! Please check your email for an activation link to complete your registration.'
            });
        } catch (error) {
            handleError(error);
        } finally {
            setIsButtonLoading(false);
        }
    };

    // Help user navigate to login form after a 409 error
    const redirectToLogin = () => {
        navigate('/login');
    };

    return (
        <div className="login-page">
            <div className="login-container">
                <img src={logo} alt="AIvatar Logo" className="login-logo" />
                <h2>Create an account</h2>
                <p>Enter your email to create an account</p>

                <form onSubmit={handleSignup}>
                    <div className="input-group">
                        <div className="icon-container">
                            <span className="material-symbols-outlined login-icon">mail</span>
                        </div>
                        <input
                            type="email"
                            name="email"
                            placeholder="your@example.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                        />
                    </div>
                    <div className="checkbox-container">
                        <input type="checkbox" required />
                        <label>
                            I agree to the <a href="/terms-of-service" target="_blank" rel="noopener noreferrer">Terms and Conditions</a>
                        </label>
                    </div>

                    <div style={{ marginTop: '30px' }}>
                        <button 
                            className="button-plain" 
                            type="submit" 
                            disabled={isButtonLoading}
                        >
                            <span className="material-symbols-outlined">person_add</span>
                            {isButtonLoading ? 'Requesting...' : 'Request activation email'}
                        </button>
                    </div>
                </form>

                {message.text && (
                    <div className={message.type === 'error' ? 'error-message' : 'success-message'}>
                        {message.text}
                        {message.type === 'error' && message.text.includes('already registered') && (
                            <div className="error-action">
                                <button className="button-link" onClick={redirectToLogin}>
                                    Go to login page
                                </button>
                            </div>
                        )}
                    </div>
                )}

                <div className="signup-info">
                    <p>When you register:</p>
                    <ul>
                        <li>We'll send you a verification link via email</li>
                        <li>You can create AI avatars immediately after verification</li>
                        <li>Your email is only used for account access and important updates</li>
                    </ul>
                </div>

                <p className="login-toggle-text">
                    Already have an account? <strong onClick={() => navigate('/login')}>Login</strong>
                </p>

                <div className="login-separator">
                    <span>or</span>
                </div>

                <GoogleLogin />
            </div>
        </div>
    );
};

export default Signup;
